import React, { useCallback, useEffect } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import type { EHintPath } from 'v2/interfaces/hint';
import { hintFactory } from 'v2/services/hint';

interface IProps {
  inputName: string;
  children: React.ReactNode;
}

export const useHint = (path: EHintPath) => {
  useEffect(() => {
    hintFactory.loadHint(path);
  }, [path]);
};

export const Hint = ({ children, inputName }: IProps) => {
  useEffect(() => {
    hintFactory.setHint(inputName);

    return () => hintFactory.removeHint(inputName);
  }, [inputName]);

  const [hasHint] = useObservable(() => hintFactory.hasHint(inputName), [inputName]);
  const [hint] = useObservable(() => hintFactory.getHint(inputName), [inputName]);
  const [onMouseEnter] = useCallbackObservable(inpName => hintFactory.onMouseEnter(inpName), []);
  const [onMouseLeave] = useCallbackObservable(() => hintFactory.onMouseLeave(), []);

  const handleMouseEnter = useCallback(() => onMouseEnter(inputName), [inputName, onMouseEnter]);

  if (!hint) {
    return <>{children}</>;
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={onMouseLeave}>
      <div
        id='hint'
        className='shadow-card absolute right-0 z-40 mr-4 w-1/4 rounded-xs bg-white p-4 before:absolute before:top-3 before:-left-3 before:z-50 before:border-y-[12px] before:border-r-[12px] before:border-y-transparent before:border-r-white before:content-[""]'
        style={{
          opacity: hasHint ? 1 : 0,
          transform: hasHint ? 'scale(1)' : 'scale(0.75)',
          transition: 'opacity 300ms, transform 300ms'
        }}
      >
        <div className='text-text-heading text-left text-[1.1rem] font-medium'>{hint.label}</div>
        <div>{hint.hint}</div>

        {!!hint.link && !!hint.link_description && (
          <div className='mt-2.5'>
            <a href={hint.link} className='text-[0.85rem]' rel='noopener noreferrer' target='_blank'>
              {hint.link_description}
            </a>
          </div>
        )}
      </div>
      {children}
    </div>
  );
};
